import React, { useEffect, useState, useMemo } from 'react';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';

import PrintIcon from '@mui/icons-material/Print';

import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import { lightGreen, red } from '@mui/material/colors';
import { useLayout } from '../../context/LayoutContext';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

import { useGetShiftRequestsByShiftIdQuery } from '../../slices/shiftRequestApiSlice';
import { useGetAgenciesQuery } from '../../slices/agencyApiSlice';
import { useAssignShiftToAgencyMutation } from '../../slices/shiftApiSlice';
import {
  useGetShiftsQuery,
  useAssignShiftToUserMutation,
  useReOpenShiftMutation,
} from '../../slices/shiftApiSlice';
import { useUpdateShiftRequestStatusMutation } from '../../slices/shiftRequestApiSlice';
import { useGetHospitalLocationsQuery } from '../../slices/hospitalLocationApiSlice';

// Styled DataGrid with conditional row styles
const StyledDataGrid = styled(DataGrid)`
  & .${gridClasses.row}.lightGreenRow {
    background-color: ${lightGreen[100]};
  }
  & .${gridClasses.row}.redRow {
    background-color: ${red[100]};
  }
  & .${gridClasses.row}.lightOrangeRow {
    background-color: #ffe0b2;
  }
  & .${gridClasses.row}.greenRow {
    background-color: #c8e6c9;
  }
  & .${gridClasses.row}.darkGreenRow {
    background-color: rgb(149, 240, 152);
  }
`;

// Scrollable container styles
const scrollableTableStyle = {
  maxHeight: '70vh',
  overflowY: 'auto',
  border: '1px solid #ddd',
  borderRadius: '4px',
  position: 'relative',
  '&::-webkit-scrollbar': {
    width: '10px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '5px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
};


// Add PrintStyles component at the top level (after all imports but before the component)
const PrintStyles = () => (
  <style>{`
    @media print {
      .no-print {
        display: none !important;
      }
      .print-page {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      .analysis-summary {
        background-color: #f5f5f5 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      .stat-card {
        border: 1px solid #ddd !important;
        background-color: white !important;
      }
      table {
        border-collapse: collapse !important;
        width: 100% !important;
      }
      th, td {
        border: 1px solid #ddd !important;
        padding: 8px !important;
      }
      th {
        background-color: #1976d2 !important;
        color: white !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      .chart-container {
        page-break-inside: avoid !important;
      }
      .progress-bar {
        background-color: #4caf50 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
  `}</style>
);

const fixedHeaderStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 10,
  backgroundColor: 'white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

// Helper function to extract MongoDB OID from nested structure
const extractMongoId = (id) => {
  if (!id) return null;
  
  // If it's an object with $oid property (MongoDB OID structure)
  if (typeof id === 'object' && id.$oid) {
    return id.$oid;
  }
  
  // If it's already a string
  if (typeof id === 'string') {
    return id;
  }
  
  // If it's an object with _id property
  if (typeof id === 'object' && id._id) {
    return extractMongoId(id._id);
  }
  
  // Try to convert to string as last resort
  return id?.toString() || null;
};

const ShiftListScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const { setFluid } = useLayout();

  // State for filters
  const [filters, setFilters] = useState({
    directorate: '',
    wardDepartmentArea: '',
    categoryOfStaff: '',
    lineStatus: '',
    dateRange: {
      start: '',
      end: '',
    },
  });

  // State for modal
  const [openModal, setOpenModal] = useState(false);
  const [openModalWarning, setOpenModalWarning] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedShiftId, setSelectedShiftId] = useState(null);
  const [selectedFiller, setSelectedFiller] = useState(null);
  const [isAgencyFilling, setIsAgencyFilling] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  // API hooks
  const { data: shifts, isLoading, isError, error, refetch: refetchShifts } = useGetShiftsQuery();
  const { data: hospitalLocations, isLoading: loadingLocations } = useGetHospitalLocationsQuery();
  const { data: shiftRequests, refetch, isLoading: loadingRequests } = useGetShiftRequestsByShiftIdQuery(selectedShiftId, {
    skip: !selectedShiftId,
  });
  const { data: agencies, isLoading: loadingAgencies } = useGetAgenciesQuery();

  // Mutation hooks - Fixed: Added all needed mutations
  const [assignShiftToAgency] = useAssignShiftToAgencyMutation();
  const [assignShift] = useAssignShiftToUserMutation();
  const [reOpenShift] = useReOpenShiftMutation();
  const [updateShiftRequestStatus] = useUpdateShiftRequestStatusMutation();

   // Add state for analysis modal
  const [openAnalysisModal, setOpenAnalysisModal] = useState(false);




  // Debug logging
  useEffect(() => {
    if (shifts && shifts.length > 0) {
      console.log('Sample shift data:', shifts[0]);
      console.log('Shift hospitalLocationId:', shifts[0]?.hospitalLocationId);
      console.log('Extracted shift hospitalLocationId:', extractMongoId(shifts[0]?.hospitalLocationId));
    }
  }, [shifts]);

  useEffect(() => {
    if (hospitalLocations && hospitalLocations.length > 0) {
      console.log('Sample hospital location data:', hospitalLocations[0]);
      console.log('Hospital location _id:', hospitalLocations[0]?._id);
      console.log('Extracted hospital location _id:', extractMongoId(hospitalLocations[0]?._id));
    }
  }, [hospitalLocations]);

  // Create a map of hospital location IDs to their data for quick lookup
  const hospitalLocationMap = useMemo(() => {
    if (!hospitalLocations) return {};
    const map = {};
    
    hospitalLocations.forEach(location => {
      // Extract the MongoDB ID from the nested structure
      const locationId = extractMongoId(location._id);
      
      if (locationId) {
        map[locationId] = {
          directorate: location.directorate,
          department: location.department,
          wardDepartmentArea: location.wardDepartmentArea,
          costCentreCode: location.costCentreCode,
          activeYorN: location.activeYorN,
          // Store the original location for debugging
          _originalId: location._id,
          _extractedId: locationId
        };
      }
    });
    
    console.log('Hospital location map size:', Object.keys(map).length);
    console.log('First few location IDs in map:', Object.keys(map).slice(0, 5));
    
    return map;
  }, [hospitalLocations]);



  // Enrich shifts with hospital location data
  const enrichedShifts = useMemo(() => {
    if (!shifts || !hospitalLocations) {
      console.log('Missing data - shifts:', shifts, 'locations:', hospitalLocations);
      return [];
    }
    
    console.log('Enriching shifts...');
    console.log('Total shifts:', shifts.length);
    console.log('Total hospital locations:', hospitalLocations.length);
    
    const enriched = shifts.map((shift, index) => {
      // Extract the hospital location ID from the shift
      const shiftLocationId = extractMongoId(shift.hospitalLocationId);
      const locationData = shiftLocationId ? hospitalLocationMap[shiftLocationId] : null;
      
      // Debug logging for first few shifts
      if (index < 3) {
        console.log(`Shift ${index}:`);
        console.log('  Original hospitalLocationId:', shift.hospitalLocationId);
        console.log('  Extracted locationId:', shiftLocationId);
        console.log('  Found location data:', !!locationData);
        if (locationData) {
          console.log('  Directorate:', locationData.directorate);
        } else {
          console.log('  Available location IDs:', Object.keys(hospitalLocationMap).slice(0, 5));
        }
      }
      
      // If location data found, use it
      let directorate = locationData?.directorate || 'N/A';
      let displayWardDepartmentArea = shift.wardDepartmentArea || locationData?.wardDepartmentArea || 'N/A';
      let department = locationData?.department || 'N/A';
      
      // Fallback: Try to find by matching wardDepartmentArea
      if (directorate === 'N/A' && shift.wardDepartmentArea) {
        const matchedLocation = hospitalLocations.find(loc => 
          loc.wardDepartmentArea === shift.wardDepartmentArea
        );
        if (matchedLocation) {
          directorate = matchedLocation.directorate;
          department = matchedLocation.department;
          console.log(`Found directorate by ward match: ${directorate} for ${shift.wardDepartmentArea}`);
        }
      }
      
      return {
        ...shift,
        directorate,
        department,
        hospitalWardDepartmentArea: locationData?.wardDepartmentArea || 'N/A',
        displayWardDepartmentArea,
        // Store debugging info
        _originalHospitalLocationId: shift.hospitalLocationId,
        _extractedHospitalLocationId: shiftLocationId,
        _foundLocation: !!locationData
      };
    });
    
    // Count how many shifts have directorate info
    const withDirectorate = enriched.filter(s => s.directorate !== 'N/A').length;
    console.log(`Shifts with directorate info: ${withDirectorate}/${enriched.length}`);
    console.log('Sample enriched shifts:', enriched.slice(0, 3));
    
    return enriched;
  }, [shifts, hospitalLocations, hospitalLocationMap]);

  

  // Extract unique filter options from enriched data
  const filterOptions = useMemo(() => {
    if (!enrichedShifts || enrichedShifts.length === 0) {
      console.log('No enriched shifts for filter options');
      return { directorates: [], wards: [], categories: [], statuses: [] };
    }

    // Get unique directorates from enriched shifts
    const directorates = [...new Set(
      enrichedShifts
        .map(shift => shift.directorate)
        .filter(dir => dir && dir !== 'N/A')
    )].sort();
    
    // Get unique wards from enriched shifts
    const wards = [...new Set(
      enrichedShifts
        .map(shift => shift.displayWardDepartmentArea)
        .filter(ward => ward && ward !== 'N/A')
    )].sort();
    
    // Get unique categories from shifts
    const categories = [...new Set(
      enrichedShifts
        .map(shift => shift.categoryOfStaff)
        .filter(cat => cat)
    )].sort();
    
    // Get unique statuses from shifts
    const statuses = [...new Set(
      enrichedShifts
        .map(shift => shift.lineStatus)
        .filter(status => status)
    )].sort();

    console.log('Filter options:', {
      directorates,
      wardsCount: wards.length,
      categories,
      statuses
    });

    return { directorates, wards, categories, statuses };
  }, [enrichedShifts]);

  // Filter shifts based on filter criteria
  const filteredShifts = useMemo(() => {
    if (!enrichedShifts) return [];

    return enrichedShifts.filter(shift => {
      // Filter by directorate
      if (filters.directorate && shift.directorate !== filters.directorate) {
        return false;
      }
      
      // Filter by ward/department area
      if (filters.wardDepartmentArea && shift.displayWardDepartmentArea !== filters.wardDepartmentArea) {
        return false;
      }
      
      // Filter by staff category
      if (filters.categoryOfStaff && shift.categoryOfStaff !== filters.categoryOfStaff) {
        return false;
      }
      
      // Filter by line status
      if (filters.lineStatus && shift.lineStatus !== filters.lineStatus) {
        return false;
      }
      
      // Filter by date range
      if (filters.dateRange.start) {
        const startDate = new Date(filters.dateRange.start);
        const shiftStartDate = new Date(shift.startDateTime);
        if (shiftStartDate < startDate) return false;
      }
      
      if (filters.dateRange.end) {
        const endDate = new Date(filters.dateRange.end);
        const shiftEndDate = new Date(shift.endDateTime);
        if (shiftEndDate > endDate) return false;
      }
      
      return true;
    });
  }, [enrichedShifts, filters]);

  // Check if any filter is active
  const isFilterActive = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === 'dateRange') {
        return value.start || value.end;
      }
      return Boolean(value);
    });
  }, [filters]);

  // Move this useMemo to the top level (before any conditionals)
const analysisData = useMemo(() => {
  if (!filteredShifts || filteredShifts.length === 0) return null;

  const now = new Date();
  
  // Calculate shift counts by category
  const shiftsByDirectorate = {};
  const shiftsByWard = {};
  const shiftsByCategory = {};
  const shiftsByMonth = {};
  const shiftsByStatus = {
    'Open': 0,
    'Filled': 0,
    'Filled by Agency': 0,
    'On Hold': 0,
    'Cancelled': 0,
    'Archived': 0,
    'Complete': 0
  };

  // Initialize counts
  filteredShifts.forEach(shift => {
    // Directorate
    const directorate = shift.directorate || 'Unknown';
    shiftsByDirectorate[directorate] = (shiftsByDirectorate[directorate] || 0) + 1;
    
    // Ward/Department
    const ward = shift.displayWardDepartmentArea || 'Unknown';
    shiftsByWard[ward] = (shiftsByWard[ward] || 0) + 1;
    
    // Staff Category
    const category = shift.categoryOfStaff || 'Unknown';
    shiftsByCategory[category] = (shiftsByCategory[category] || 0) + 1;
    
    // Month
    const shiftDate = new Date(shift.startDateTime);
    const monthYear = shiftDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    shiftsByMonth[monthYear] = (shiftsByMonth[monthYear] || 0) + 1;
    
    // Status
    const status = shift.lineStatus || 'Unknown';
    const normalizedStatus = status === 'FilledbyAgency' ? 'Filled by Agency' : 
                            status === 'FilledbyStaff' ? 'Filled' : 
                            status;
    shiftsByStatus[normalizedStatus] = (shiftsByStatus[normalizedStatus] || 0) + 1;
  });

  // Calculate percentages
  const totalShifts = filteredShifts.length;
  const statusPercentages = Object.entries(shiftsByStatus).reduce((acc, [status, count]) => {
    acc[status] = {
      count,
      percentage: ((count / totalShifts) * 100).toFixed(1)
    };
    return acc;
  }, {});

  // Sort by count (descending)
  const sortedDirectorates = Object.entries(shiftsByDirectorate)
    .sort(([,a], [,b]) => b - a)
    .map(([name, count]) => ({ name, count, percentage: ((count / totalShifts) * 100).toFixed(1) }));

  const sortedWards = Object.entries(shiftsByWard)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10); // Top 10 wards

  const sortedCategories = Object.entries(shiftsByCategory)
    .sort(([,a], [,b]) => b - a)
    .map(([name, count]) => ({ name, count, percentage: ((count / totalShifts) * 100).toFixed(1) }));

  const sortedMonths = Object.entries(shiftsByMonth)
    .sort(([a], [b]) => new Date(a) - new Date(b));

  // Calculate future vs past shifts
  const futureShifts = filteredShifts.filter(shift => new Date(shift.startDateTime) > now).length;
  const pastShifts = filteredShifts.filter(shift => new Date(shift.startDateTime) <= now).length;

  return {
    totalShifts,
    futureShifts,
    pastShifts,
    shiftsByDirectorate: sortedDirectorates,
    shiftsByWard: sortedWards,
    shiftsByCategory: sortedCategories,
    shiftsByMonth: sortedMonths,
    shiftsByStatus: statusPercentages,
    dateRange: filters.dateRange,
    activeFilters: {
      directorate: filters.directorate,
      ward: filters.wardDepartmentArea,
      category: filters.categoryOfStaff,
      status: filters.lineStatus
    }
  };
}, [filteredShifts, filters]);



  // Clear all filters
  const clearFilters = () => {
    setFilters({
      directorate: '',
      wardDepartmentArea: '',
      categoryOfStaff: '',
      lineStatus: '',
      dateRange: { start: '', end: '' },
    });
  };

  // Handle filter change
  const handleFilterChange = (filterName, value) => {
    if (filterName.includes('dateRange')) {
      const [range, type] = filterName.split('.');
      setFilters(prev => ({
        ...prev,
        dateRange: {
          ...prev.dateRange,
          [type]: value,
        },
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [filterName]: value,
      }));
    }
  };

  // Column definitions
  const columns = [
    {
      field: 'startDateTime',
      headerName: 'Start Time',
      width: 180,
      type: 'dateTime',
      valueGetter: (params) => new Date(params.row.startDateTime),
      valueFormatter: (params) => new Date(params.value).toLocaleString(),
    },
    {
      field: 'endDateTime',
      headerName: 'End Time',
      width: 180,
      type: 'dateTime',
      valueGetter: (params) => new Date(params.row.endDateTime),
      valueFormatter: (params) => new Date(params.value).toLocaleString(),
    },
    { 
      field: 'displayWardDepartmentArea', 
      headerName: 'Ward/Dept Area', 
      width: 150,
      valueGetter: (params) => params.row.displayWardDepartmentArea,
    },
    {
      field: 'directorate',
      headerName: 'Directorate',
      width: 180,
      valueGetter: (params) => params.row.directorate,
    },
    { 
      field: 'categoryOfStaff', 
      headerName: 'Staff Category', 
      width: 150 
    },
    { 
      field: 'lineStatus', 
      headerName: 'Line Status', 
      width: 120 
    },
    { 
      field: 'filledByName', 
      headerName: 'Filled By', 
      width: 120 
    },
    {
      field: 'specialRequirements',
      headerName: 'Special Requirements',
      width: 150,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => {
            const hasStaff = params.row.filledByName && params.row.filledByName.trim();
            const hasAgency = params.row.filledByAgencyName && params.row.filledByAgencyName.trim();

            if (hasStaff || hasAgency) {
              setSelectedShift(params.row);
              setSelectedShiftId(params.row._id);
              setOpenModalWarning(true);
            } else {
              navigate(`/admin/shifts/${params.row._id}/edit`);
            }
          }}
        >
          Edit
        </Button>
      ),
    },
    {
      field: 'reopen',
      headerName: 'Re-Open',
      width: 100,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="warning"
          size="small"
          onClick={async () => {
            setSelectedShift(params.row);
            setSelectedShiftId(params.row._id);
            setOpenModal(true);
          }}
          disabled={params.row.lineStatus === 'Open'}
        >
          {params.row.lineStatus === 'Open' ? 'Already Open' : 'Re-Open'}
        </Button>
      ),
    },
  ];


  




  // Row styling function
  const getRowClassName = (params) => {
    const now = new Date();
    const end = new Date(params.row.endDateTime);
    if (end < now) return 'redRow';

    const status = params.row.lineStatus?.toLowerCase();
    switch (status) {
      case 'open':
      case 'on hold':
        return 'lightOrangeRow';
      case 'archived':
      case 'filledbystaff':
      case 'filled':
      case 'complete':
        return 'greenRow';
      case 'filledbyagency':
        return 'darkGreenRow';
      case 'cancelled':
        return 'redRow';
      default:
        return 'lightGreenRow';
    }
  };

  // Handle shift reset
  const handleReset = async () => {
    try {
      await reOpenShift({
        shiftId: selectedShiftId,
        managerId: userInfo._id,
        lineStatus: 'Open',
      }).unwrap();

      // Find and update any approved request
      const approvedRequest = shiftRequests?.find(req => req.lineStatus === "Approved");
      if (approvedRequest) {
        await updateShiftRequestStatus({
          shiftRequestId: approvedRequest._id,
          lineStatus: "Open",
        }).unwrap();
      }

      await refetch();
      await refetchShifts();

      setOpenModal(false);
      setSelectedShift(null);
      setSelectedShiftId(null);
      toast.success('Shift reset successfully!');
    } catch (err) {
      console.error('Failed to reset shift:', err);
      toast.error('Failed to reset shift');
    }
  };

  

  useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  if (isLoading || loadingLocations) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Typography color="error">Error: {error.message}</Typography>
      </Box>
    );
  }



 



 // Handle print analysis
  const handlePrintAnalysis = () => {
    setOpenAnalysisModal(true);
  };

  // Print analysis function - MODERN VERSION
const printAnalysisReport = () => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    toast.error('Please allow pop-ups to print');
    return;
  }

  const reportHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Shift Analysis Report</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 20px;
          color: #333;
        }
        .header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 2px solid #1976d2;
          padding-bottom: 20px;
        }
        .header h1 {
          color: #1976d2;
          margin-bottom: 5px;
        }
        .summary-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-bottom: 30px;
        }
        .stat-card {
          background: #f5f5f5;
          padding: 20px;
          border-radius: 8px;
          text-align: center;
        }
        .stat-number {
          font-size: 32px;
          font-weight: bold;
          color: #1976d2;
          margin: 10px 0;
        }
        .stat-label {
          font-size: 14px;
          color: #666;
        }
        .section {
          margin-bottom: 30px;
          page-break-inside: avoid;
        }
        .section-title {
          background: #1976d2;
          color: white;
          padding: 10px;
          border-radius: 4px;
          margin-bottom: 15px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 15px;
        }
        th, td {
          border: 1px solid #ddd;
          padding: 10px;
          text-align: left;
        }
        th {
          background-color: #1976d2;
          color: white;
        }
        tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .progress-bar {
          height: 20px;
          background-color: #e0e0e0;
          border-radius: 10px;
          margin: 10px 0;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          background-color: #4caf50;
          border-radius: 10px;
          text-align: center;
          color: white;
          line-height: 20px;
          font-size: 12px;
        }
        .filter-info {
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          padding: 15px;
          border-radius: 5px;
          margin-bottom: 20px;
        }
        .timestamp {
          color: #666;
          font-size: 12px;
          text-align: right;
          margin-top: 30px;
        }
        .print-button {
          position: fixed;
          top: 10px;
          right: 10px;
          padding: 10px 20px;
          background: #1976d2;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          z-index: 1000;
        }
        .print-button:hover {
          background: #1565c0;
        }
        @media print {
          @page {
            margin: 0.5in;
            size: landscape;
          }
          body {
            font-size: 12px;
          }
          .summary-grid {
            grid-template-columns: repeat(4, 1fr);
          }
          .print-button {
            display: none !important;
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Shift Analysis Report</h1>
        <p>Generated: ${new Date().toLocaleString()}</p>
        <p>Total Shifts Analyzed: ${analysisData?.totalShifts || 0}</p>
      </div>

      ${analysisData ? `
        <div class="filter-info">
          <h3>Analysis Filters</h3>
          <p><strong>Date Range:</strong> ${analysisData.dateRange.start || 'All dates'} to ${analysisData.dateRange.end || 'All dates'}</p>
          <p><strong>Directorate:</strong> ${analysisData.activeFilters.directorate || 'All directorates'}</p>
          <p><strong>Ward/Dept:</strong> ${analysisData.activeFilters.ward || 'All wards'}</p>
          <p><strong>Staff Category:</strong> ${analysisData.activeFilters.category || 'All categories'}</p>
          <p><strong>Line Status:</strong> ${analysisData.activeFilters.status || 'All statuses'}</p>
        </div>

        <div class="summary-grid">
          <div class="stat-card">
            <div class="stat-number">${analysisData.totalShifts}</div>
            <div class="stat-label">Total Shifts</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">${analysisData.futureShifts}</div>
            <div class="stat-label">Future Shifts</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">${analysisData.pastShifts}</div>
            <div class="stat-label">Past Shifts</div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">
            <h2>Shift Status Distribution</h2>
          </div>
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th>Count</th>
                <th>Percentage</th>
                <th>Visual</th>
              </tr>
            </thead>
            <tbody>
              ${Object.entries(analysisData.shiftsByStatus)
                .map(([status, data]) => `
                  <tr>
                    <td>${status}</td>
                    <td>${data.count}</td>
                    <td>${data.percentage}%</td>
                    <td>
                      <div class="progress-bar">
                        <div class="progress-fill" style="width: ${data.percentage}%">
                          ${data.percentage}%
                        </div>
                      </div>
                    </td>
                  </tr>
                `).join('')}
            </tbody>
          </table>
        </div>

        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px;">
          <div class="section">
            <div class="section-title">
              <h2>Top Directorates (by shift count)</h2>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Directorate</th>
                  <th>Shifts</th>
                  <th>% of Total</th>
                </tr>
              </thead>
              <tbody>
                ${analysisData.shiftsByDirectorate
                  .slice(0, 10)
                  .map(dir => `
                    <tr>
                      <td>${dir.name}</td>
                      <td>${dir.count}</td>
                      <td>${dir.percentage}%</td>
                    </tr>
                  `).join('')}
              </tbody>
            </table>
          </div>

          <div class="section">
            <div class="section-title">
              <h2>Staff Categories</h2>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Shifts</th>
                  <th>% of Total</th>
                </tr>
              </thead>
              <tbody>
                ${analysisData.shiftsByCategory
                  .slice(0, 10)
                  .map(cat => `
                    <tr>
                      <td>${cat.name}</td>
                      <td>${cat.count}</td>
                      <td>${cat.percentage}%</td>
                    </tr>
                  `).join('')}
              </tbody>
            </table>
          </div>
        </div>

        <div class="section">
          <div class="section-title">
            <h2>Monthly Shift Distribution</h2>
          </div>
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Shifts</th>
                <th>Trend</th>
              </tr>
            </thead>
            <tbody>
              ${analysisData.shiftsByMonth
                .map(([month, count]) => `
                  <tr>
                    <td>${month}</td>
                    <td>${count}</td>
                    <td>
                      <div class="progress-bar">
                        <div class="progress-fill" style="width: ${(count / Math.max(...analysisData.shiftsByMonth.map(([,c]) => c))) * 100}%">
                          ${count}
                        </div>
                      </div>
                    </td>
                  </tr>
                `).join('')}
            </tbody>
          </table>
        </div>

        <div class="section">
          <div class="section-title">
            <h2>Top 10 Wards/Departments</h2>
          </div>
          <table>
            <thead>
              <tr>
                <th>Ward/Department</th>
                <th>Shifts</th>
                <th>% of Total</th>
              </tr>
            </thead>
            <tbody>
              ${analysisData.shiftsByWard
                .map(([ward, count]) => {
                  const percentage = ((count / analysisData.totalShifts) * 100).toFixed(1);
                  return `
                    <tr>
                      <td>${ward}</td>
                      <td>${count}</td>
                      <td>${percentage}%</td>
                    </tr>
                  `;
                }).join('')}
            </tbody>
          </table>
        </div>

        <div class="section">
          <div class="section-title">
            <h2>Filling Rate Analysis</h2>
          </div>
          <table>
            <thead>
              <tr>
                <th>Metric</th>
                <th>Value</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Fill Rate</td>
                <td>${(((analysisData.shiftsByStatus['Filled']?.count || 0) + 
                        (analysisData.shiftsByStatus['Filled by Agency']?.count || 0)) / 
                        analysisData.totalShifts * 100).toFixed(1)}%</td>
                <td>Percentage of shifts filled (staff or agency)</td>
              </tr>
              <tr>
                <td>Agency Fill Rate</td>
                <td>${((analysisData.shiftsByStatus['Filled by Agency']?.count || 0) / 
                        analysisData.totalShifts * 100).toFixed(1)}%</td>
                <td>Percentage filled by external agencies</td>
              </tr>
              <tr>
                <td>Open Rate</td>
                <td>${((analysisData.shiftsByStatus['Open']?.count || 0) / 
                        analysisData.totalShifts * 100).toFixed(1)}%</td>
                <td>Percentage still open/unfilled</td>
              </tr>
            </tbody>
          </table>
        </div>
      ` : '<p>No data available for analysis</p>'}

      <div class="timestamp">
        Report generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
      </div>

      <button class="print-button" onclick="window.print()">Print Report</button>
    </body>
    </html>
  `;

  // Modern way to write HTML to a new window
  printWindow.document.open();
  printWindow.document.write(reportHTML);
  printWindow.document.close();
  
  // Focus the window
  printWindow.focus();
};


  return (
    
        <Box sx={{ height: 'calc(100vh - 100px)', width: '100%', p: 2 }}>
      <PrintStyles />
      
      {/* Header with Create Button, Print Analysis, and Filter Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/admin/shifts/create')}
        >
          Create Shift
        </Button>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Print Analysis Button */}
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrintAnalysis}
            disabled={!filteredShifts || filteredShifts.length === 0}
            sx={{
              bgcolor: '#673ab7',
              color: 'white',
              '&:hover': {
                bgcolor: '#5e35b1'
              },
              '&.Mui-disabled': {
                bgcolor: 'grey.300',
                color: 'grey.500'
              }
            }}
          >
            Print Analysis
          </Button>
          
          <Tooltip title="Toggle Filters">
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters {isFilterActive && (
                <Chip 
                  size="small" 
                  label="Active" 
                  color="primary" 
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} 
                />
              )}
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Filter Panel */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Directorate</InputLabel>
                <Select
                  value={filters.directorate}
                  label="Directorate"
                  onChange={(e) => handleFilterChange('directorate', e.target.value)}
                >
                  <MenuItem value="">All Directorates</MenuItem>
                  {filterOptions.directorates.map(dir => (
                    <MenuItem key={dir} value={dir}>{dir}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Ward/Dept Area</InputLabel>
                <Select
                  value={filters.wardDepartmentArea}
                  label="Ward/Dept Area"
                  onChange={(e) => handleFilterChange('wardDepartmentArea', e.target.value)}
                >
                  <MenuItem value="">All Areas</MenuItem>
                  {filterOptions.wards.map(ward => (
                    <MenuItem key={ward} value={ward}>{ward}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Staff Category</InputLabel>
                <Select
                  value={filters.categoryOfStaff}
                  label="Staff Category"
                  onChange={(e) => handleFilterChange('categoryOfStaff', e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {filterOptions.categories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Line Status</InputLabel>
                <Select
                  value={filters.lineStatus}
                  label="Line Status"
                  onChange={(e) => handleFilterChange('lineStatus', e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {filterOptions.statuses.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                value={filters.dateRange.start}
                onChange={(e) => handleFilterChange('dateRange.start', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                value={filters.dateRange.end}
                onChange={(e) => handleFilterChange('dateRange.end', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  disabled={!isFilterActive}
                >
                  Clear Filters
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setShowFilters(false)}
                >
                  Apply
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Results Summary */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" color="textSecondary">
          Showing {filteredShifts.length} of {shifts?.length || 0} shifts
          {filters.directorate && ` • Directorate: ${filters.directorate}`}
          {filters.wardDepartmentArea && ` • Area: ${filters.wardDepartmentArea}`}
          {filters.categoryOfStaff && ` • Category: ${filters.categoryOfStaff}`}
          {filters.lineStatus && ` • Status: ${filters.lineStatus}`}
        </Typography>
        
        {isFilterActive && (
          <Chip 
            label={`${filteredShifts.length} filtered`}
            color="primary"
            variant="outlined"
            size="small"
          />
        )}
      </Box>

      {/* Main Content with Fixed Header and Scrollable Body */}
      <Card elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <CardHeader 
          title="Shift List" 
          subtitle={`Shifts with directorate info: ${enrichedShifts.filter(s => s.directorate !== 'N/A').length}/${enrichedShifts.length}`}
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            borderBottom: '1px solid #ddd',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        />
        
        {/* Fixed Header Row */}
        <Box sx={fixedHeaderStyle}>
          <Grid container sx={{ bgcolor: 'grey.100', borderBottom: 1, borderColor: 'divider' }}>
            {columns.map((column) => (
              <Grid item key={column.field} sx={{ 
                width: column.width, 
                minWidth: column.width,
                p: 1,
                borderRight: 1,
                borderColor: 'divider',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
              }}>
                {column.headerName}
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Scrollable Data Grid */}
        <Box sx={scrollableTableStyle}>
          <StyledDataGrid
            density="compact"
            rows={filteredShifts}
            columns={columns}
            getRowId={(row) => extractMongoId(row._id) || row._id}
            getRowClassName={getRowClassName}
            hideFooter={true}
            disableSelectionOnClick
            disableColumnMenu
            disableDensitySelector
            onRowClick={(params) => {
              setSelectedShift(params.row);
              setSelectedShiftId(extractMongoId(params.row._id) || params.row._id);
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          />
          
          {/* Show message if no results */}
          {filteredShifts.length === 0 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 200,
              color: 'text.secondary',
              flexDirection: 'column',
              gap: 1,
            }}>
              <Typography variant="h6">No shifts found</Typography>
              <Typography variant="body2">
                {isFilterActive 
                  ? "No shifts match your current filters. Try adjusting or clearing filters."
                  : "No shifts available. Create a new shift to get started."}
              </Typography>
              {isFilterActive && (
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={clearFilters}
                  startIcon={<ClearIcon />}
                >
                  Clear All Filters
                </Button>
              )}
            </Box>
          )}
        </Box>
        
        {/* Footer with pagination info */}
        <Box sx={{ 
          p: 1, 
          borderTop: 1, 
          borderColor: 'divider',
          bgcolor: 'grey.50',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Typography variant="caption" color="textSecondary">
            Total: {filteredShifts.length} shifts
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Scroll to see more shifts
          </Typography>
        </Box>
      </Card>

      {/* Re-Open Shift Modal */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Re-Open Shift
          <IconButton
            aria-label="close"
            onClick={() => setOpenModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedShift ? (
            <Box>
              <Typography variant="body1"><strong>Start:</strong> {new Date(selectedShift.startDateTime).toLocaleString()}</Typography>
              <Typography variant="body1"><strong>End:</strong> {new Date(selectedShift.endDateTime).toLocaleString()}</Typography>
              <Typography variant="body1"><strong>Ward/Dept:</strong> {selectedShift.displayWardDepartmentArea}</Typography>
              <Typography variant="body1"><strong>Directorate:</strong> {selectedShift.directorate}</Typography>
              <Typography variant="body1"><strong>Department:</strong> {selectedShift.department}</Typography>
              <Typography variant="body1"><strong>Category of Staff:</strong> {selectedShift.categoryOfStaff}</Typography>
              <Typography variant="body1"><strong>Line Status:</strong> {selectedShift.lineStatus}</Typography>
              <Typography variant="body1"><strong>Filled By:</strong> {selectedShift.filledByName || selectedShift.filledByAgencyName || 'Unassigned'}</Typography>
              <Typography variant="body1"><strong>Special Requirements:</strong> {selectedShift.specialRequirements || 'None'}</Typography>
              <Typography variant="body1"><strong>Original Hospital Location ID:</strong> {JSON.stringify(selectedShift._originalHospitalLocationId)}</Typography>
              <Typography variant="body1"><strong>Extracted Hospital Location ID:</strong> {selectedShift._extractedHospitalLocationId || 'None'}</Typography>
              <Typography variant="body1"><strong>Location Found:</strong> {selectedShift._foundLocation ? 'Yes' : 'No'}</Typography>
            </Box>
          ) : (
            <Typography>No shift selected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button onClick={handleReset} color="warning" variant="contained">
            Confirm Re-Open
          </Button>
        </DialogActions>
      </Dialog>

     {/* Warning Modal for Filled Shifts */}
    <Dialog open={openModalWarning} onClose={() => setOpenModalWarning(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        Shift Already Filled
        <IconButton
          aria-label="close"
          onClick={() => setOpenModalWarning(false)}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography gutterBottom>
          This shift is currently assigned to {selectedShift?.filledByName || selectedShift?.filledByAgencyName}.
        </Typography>
        <Typography gutterBottom>
          To make changes, you need to <strong>re-open</strong> the shift. This will:
          <ul>
            <li>Remove the assigned staff/agency</li>
            <li>Make the shift available again for requests</li>
          </ul>
        </Typography>
        <Typography color="error">
          Warning: This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenModalWarning(false)} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => {
            setOpenModalWarning(false);
            setOpenModal(true);
          }}
        >
          Re-Open Shift
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* Analysis Modal - MOVED HERE, outside Warning Modal */}
    <Dialog 
      open={openAnalysisModal} 
      onClose={() => setOpenAnalysisModal(false)} 
      maxWidth="lg" 
      fullWidth
    >
      <DialogTitle>
        Shift Analysis Report
        <IconButton
          aria-label="close"
          onClick={() => setOpenAnalysisModal(false)}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {analysisData ? (
          <Box>
            {/* ... Analysis Modal content ... */}
          </Box>
        ) : (
          <Typography>No analysis data available.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenAnalysisModal(false)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={printAnalysisReport}
          disabled={!analysisData}
        >
          Print Report
        </Button>
      </DialogActions>
    </Dialog>
  </Box>
);
};

export default ShiftListScreen;


 